import { PropsWithChildren } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import classNames from 'classnames';

import ButtonClean from '~/components/ButtonClean';
import NewTabOpener from '~/components/NewTabOpener';

import { ReactComponent as ChevronIcon } from '~/assets/images/iconsLibrary/chevron-left.svg';

import styles from './MenuOption.module.scss';

type TCommonMenuOptionProps = {
  icon?: JSX.Element;
  className?: string;
  label: string;
  labelClassName?: string;
};

type TMenuOptionVariant =
  | { onClick: () => void; to?: never; href?: never }
  | { href: string; onClick?: never; to?: never }
  | { to: LinkProps['to']; onClick?: never; href?: never }
  | { to?: never; onClick?: never; href?: never };

function MenuOptionChildren({
  children,
  label,
  icon,
  labelClassName,
}: PropsWithChildren<TCommonMenuOptionProps>): JSX.Element {
  return (
    <>
      {icon}
      <p className={classNames(styles.label, labelClassName)}>{label}</p>
      <div className={styles.component}>
        {children ?? <ChevronIcon className={styles.arrowIcon} />}
      </div>
    </>
  );
}

export function MenuOption(
  props: PropsWithChildren<TCommonMenuOptionProps & TMenuOptionVariant>,
): JSX.Element {
  const {
    href,
    to,
    onClick,
    className: customClassName,
    ...commonProps
  } = props;

  const className = classNames(
    styles.container,
    customClassName,
    !commonProps.icon && styles.secondary,
  );

  if (href) {
    return (
      <NewTabOpener
        href={href}
        className={classNames(className, styles.externalLink)}
      >
        <MenuOptionChildren {...commonProps} />
      </NewTabOpener>
    );
  }

  if (to) {
    return (
      <Link
        to={to}
        className={classNames(className, styles.internalLink)}
      >
        <MenuOptionChildren {...commonProps} />
      </Link>
    );
  }

  if (onClick) {
    return (
      <ButtonClean
        onClick={onClick}
        className={classNames(className, styles.button)}
      >
        <MenuOptionChildren {...commonProps} />
      </ButtonClean>
    );
  }

  return (
    <div className={className}>
      <MenuOptionChildren {...commonProps} />
    </div>
  );
}
